import React,{useState,useEffect} from 'react';
import {Link,useLocation} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {useAuth} from '../../contexts/AuthContext';
import {useClinic} from '../../contexts/ClinicContext';

const {
  FiHome,FiUsers,FiCalendar,FiFileText,FiUserCheck,FiBarChart3,FiX,FiInfo,FiPhone,FiImage,FiSettings,FiTrendingDown,FiBookOpen
}=FiIcons;

const Sidebar=({isOpen,onClose})=> {
  const location=useLocation();
  const {user}=useAuth();
  const {clinic_name,logo_url}=useClinic();
  const [isMobile,setIsMobile]=useState(false);

  // Check if we're on mobile based on window width
  useEffect(()=> {
    const checkIsMobile=()=> {
      setIsMobile(window.innerWidth < 1024);
    };
    // Initial check
    checkIsMobile();
    // Add event listener for window resize
    window.addEventListener('resize',checkIsMobile);
    // Clean up event listener
    return ()=> window.removeEventListener('resize',checkIsMobile);
  },[]);

  const menuItems=[
    {name: 'Dashboard',href: '/dashboard',icon: FiHome,roles: ['admin','manager','receptionist','doctor']},
    {name: 'Patients',href: '/patients',icon: FiUsers,roles: ['admin','manager','receptionist']},
    {name: 'Appointments',href: '/appointments',icon: FiCalendar,roles: ['admin','manager','receptionist','doctor']},
    {name: 'Invoices',href: '/invoices',icon: FiFileText,roles: ['admin','manager','receptionist']},
    {name: 'Expenses',href: '/expenses',icon: FiTrendingDown,roles: ['admin','manager']},
    {name: 'Accounts',href: '/accounts',icon: FiBookOpen,roles: ['admin','manager']},
    {name: 'Doctors',href: '/doctors',icon: FiUserCheck,roles: ['admin','doctor']},
    {name: 'Reports',href: '/reports',icon: FiBarChart3,roles: ['admin','manager']},
    {name: 'Settings',href: '/settings',icon: FiSettings,roles: ['admin']},
  ];

  const publicItems=[
    {name: 'About Us',href: '/about',icon: FiInfo},
    {name: 'Contact',href: '/contact',icon: FiPhone},
    {name: 'Gallery',href: '/gallery',icon: FiImage},
  ];

  const filteredMenuItems=menuItems.filter(item=> item.roles.includes(user?.role) );

  // Get clinic name initials for the logo fallback
  const getInitials=(name)=> {
    return name
      .split(' ')
      .map(word=> word[0])
      .join('')
      .substring(0,2)
      .toUpperCase();
  };

  // Render different sidebar for mobile vs desktop
  const renderSidebarContent=()=> (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between h-16 px-6 border-b border-grey-200">
        <div className="flex items-center">
          {logo_url ? (
            <div className="w-8 h-8 overflow-hidden rounded-lg flex items-center justify-center">
              <img src={logo_url} alt={clinic_name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-8 h-8 bg-maroon-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">{getInitials(clinic_name)}</span>
            </div>
          )}
          <span className="ml-2 text-lg font-semibold text-grey-800">
            {clinic_name.length > 15 ? `${clinic_name.substring(0,15)}...` : clinic_name}
          </span>
        </div>
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 rounded-md text-grey-400 hover:text-grey-600 hover:bg-grey-100"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        )}
      </div>
      <nav className="mt-6 px-3 flex-1 overflow-y-auto">
        <div className="space-y-1">
          {filteredMenuItems.map((item)=> {
            const isActive=location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={isMobile ? onClose : undefined}
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-maroon-100 text-maroon-700 border-r-2 border-maroon-600'
                    : 'text-grey-600 hover:bg-grey-100 hover:text-grey-900'
                }`}
              >
                <SafeIcon
                  icon={item.icon}
                  className={`mr-3 w-5 h-5 ${
                    isActive ? 'text-maroon-600' : 'text-grey-400 group-hover:text-grey-500'
                  }`}
                />
                {item.name}
              </Link>
            );
          })}
        </div>
        <div className="mt-8 pt-6 border-t border-grey-200">
          <div className="space-y-1">
            {publicItems.map((item)=> {
              const isActive=location.pathname===item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={isMobile ? onClose : undefined}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-maroon-100 text-maroon-700'
                      : 'text-grey-600 hover:bg-grey-100 hover:text-grey-900'
                  }`}
                >
                  <SafeIcon
                    icon={item.icon}
                    className={`mr-3 w-5 h-5 ${
                      isActive ? 'text-maroon-600' : 'text-grey-400 group-hover:text-grey-500'
                    }`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );

  // If mobile,use animation and conditional rendering
  // If desktop,always render sidebar
  return (
    <>
      {/* Mobile overlay - only shown on mobile */}
      {isMobile && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
              onClick={onClose}
            />
          )}
        </AnimatePresence>
      )}
      {/* Sidebar - different rendering for mobile vs desktop */}
      {isMobile ? (
        <motion.div
          initial={{x: -280}}
          animate={{x: isOpen ? 0 : -280}}
          transition={{type: 'spring',stiffness: 300,damping: 30}}
          className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg"
        >
          {renderSidebarContent()}
        </motion.div>
      ) : (
        // Desktop sidebar - always visible,no animations
        <div className="w-64 bg-white shadow-lg">
          {renderSidebarContent()}
        </div>
      )}
    </>
  );
};

export default Sidebar;